/**
 * Video Auth Layout Route
 * Provides authentication without the AppLayout sidebar.
 * Video rooms need full-screen immersive experience like Google Meet/Teams.
 */
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_videoAuth")({
  component: VideoAuthLayout,
});

function VideoAuthLayout() {
  const navigate = useNavigate();
  const { user, isLoading, isAuthenticated, notAuthorizedForApp, authorizationErrorMessage } = useAuth();

  // Redirect to login if not authenticated (after loading completes)
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !notAuthorizedForApp) {
      navigate({
        to: "/login",
        search: { redirect: location.pathname },
      });
    }
  }, [isLoading, isAuthenticated, notAuthorizedForApp, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-950">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (notAuthorizedForApp) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-950 p-6 text-center text-white">
        <div className="max-w-md">
          <h1 className="text-2xl font-semibold">Kein Zugriff</h1>
          <p className="mt-2 text-sm text-white/80">
            {authorizationErrorMessage ??
              "Ihr Konto hat keinen Zugriff auf diese Anwendung. Administratoren nutzen bitte das Admin-Portal."}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (user && ["administrator", "super_admin"].includes(user.role)) {
    return null;
  }

  // Full-screen layout - no sidebar, no header
  return <Outlet />;
}
